<script lang="ts">
  import type { ExecutionSummaryData } from '../../services/s3-client';
  import StatusBadge from '../shared/StatusBadge.svelte';

  interface Props {
    summary: ExecutionSummaryData;
    onnodeclick?: (nodeId: string) => void;
  }

  let { summary, onnodeclick }: Props = $props();

  function formatMs(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const secs = ms / 1000;
    if (secs < 60) return `${secs.toFixed(1)}s`;
    return `${Math.floor(secs / 60)}m ${Math.floor(secs % 60)}s`;
  }

  function statusType(status: string): 'success' | 'error' | 'pending' {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'error';
    return 'pending';
  }

  /** Width percentage relative to longest node */
  function barWidth(node: { duration_ms: number }): number {
    const max = Math.max(...summary.nodes.map(n => n.duration_ms));
    return max > 0 ? Math.max(4, (node.duration_ms / max) * 100) : 4;
  }
</script>

<div class="bg-bg-card border border-border rounded-xl p-4">
  <!-- Header -->
  <div class="flex items-start justify-between mb-4">
    <div>
      <h3 class="text-sm font-semibold text-text-primary">{summary.workflow_name}</h3>
      <p class="text-[10px] font-mono text-text-muted mt-0.5">{summary.execution_id}</p>
    </div>
    <div class="text-right">
      <span class="text-sm font-semibold text-accent">{formatMs(summary.total_duration_ms)}</span>
      <p class="text-[10px] text-text-muted">{summary.node_count} nodes</p>
    </div>
  </div>

  <!-- Node pipeline -->
  <div class="space-y-1.5">
    {#each summary.nodes as node, i (node.node_id)}
      <button
        onclick={() => onnodeclick?.(node.node_id)}
        class="w-full text-left group flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
      >
        <!-- Index -->
        <span class="text-[10px] text-text-muted w-4 text-right shrink-0">{i + 1}</span>

        <!-- Status dot -->
        <span class="w-2 h-2 rounded-full shrink-0 {node.status === 'completed' ? 'bg-success' : node.status === 'failed' ? 'bg-error' : 'bg-text-muted'}"></span>

        <!-- Name -->
        <span class="text-xs text-text-primary w-36 truncate shrink-0 group-hover:text-accent transition-colors">{node.node_id}</span>

        <!-- Duration bar -->
        <div class="flex-1 h-4 relative">
          <div
            class="absolute inset-y-0 left-0 rounded-sm {node.status === 'completed' ? 'bg-accent/20' : 'bg-error/20'}"
            style="width: {barWidth(node)}%"
          ></div>
        </div>

        <!-- Duration text -->
        <span class="text-[10px] text-text-muted w-14 text-right shrink-0">{formatMs(node.duration_ms)}</span>
      </button>
    {/each}
  </div>

  <!-- Keys summary -->
  <div class="mt-3 pt-3 border-t border-border-light">
    <p class="text-[10px] text-text-muted mb-1.5">Keys added across pipeline:</p>
    <div class="flex flex-wrap gap-1">
      {#each summary.nodes.flatMap(n => n.keys_added) as key (key)}
        <span class="text-[9px] px-1.5 py-0.5 rounded bg-bg-dark border border-border-light text-text-secondary font-mono">{key}</span>
      {/each}
    </div>
  </div>
</div>
